import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, MessageCircle, ArrowLeft, Star, ChevronUp, ChevronDown, Lock, Bike, Car, Navigation } from "lucide-react";
import MapMock from "@/components/customer/MapMock";
import DriverDeliveryMap from "@/components/maps/DriverDeliveryMap";
import OrderChatDrawer from "@/components/OrderChatDrawer";
import RatingModal from "@/components/RatingModal";
import DriverProfileDrawer from "@/components/DriverProfileDrawer";

const STAGES = [
  { key: "placed", label: "Order placed", sub: "We sent your order" },
  { key: "preparing", label: "Cooking", sub: "The kitchen is on it" },
  { key: "ready", label: "Ready", sub: "Your food is ready — the driver is on their way to grab it" },
  { key: "picked_up", label: "Picked up", sub: "Driver has your food" },
  { key: "on_the_way", label: "On the way", sub: "Almost there" },
  { key: "delivered", label: "Delivered", sub: "Enjoy!" },
];

export default function TrackingPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [driverProfileOpen, setDriverProfileOpen] = useState(false);
  const ratingPromptedRef = useRef(false);
  const notifiedReadyRef = useRef(false);
  const lastDelayRef = useRef(null); // total minutes delayed we've already surfaced

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    load();
    // Faster poll while the driver is en-route so the marker + chat feel real-time.
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [orderId]);

  useEffect(() => {
    if (!order) return;
    // Auto-advance only through the kitchen stages. Once the food is "ready",
    // real actions (restaurant handoff + driver photos) drive the flow.
    const AUTO_STAGES = ["placed", "preparing"];
    if (!AUTO_STAGES.includes(order.status)) return;
    const t = setTimeout(async () => {
      const idx = STAGES.findIndex((s) => s.key === order.status);
      const next = STAGES[Math.min(idx + 1, STAGES.length - 1)];
      if (next.key !== order.status) {
        try {
          await api.put(`/orders/${orderId}/status`, { status: next.key });
          load();
        } catch {}
      }
    }, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [order]);

  // Auto-open the rating modal as soon as the delivery photo lands and status is delivered.
  useEffect(() => {
    if (!order) return;
    if (order.status !== "delivered") return;
    if (order.customer_rating != null) return; // already rated
    if (ratingPromptedRef.current) return;
    ratingPromptedRef.current = true;
    // small delay so the "Delivered" celebration lands first
    const t = setTimeout(() => setShowRating(true), 700);
    return () => clearTimeout(t);
  }, [order?.status, order?.customer_rating]);

  // Kitchen finished the food — cheer the customer once the status flips to `ready`.
  useEffect(() => {
    if (!order) return;
    if (order.status !== "ready") { notifiedReadyRef.current = false; return; }
    if (notifiedReadyRef.current) return;
    notifiedReadyRef.current = true;
    toast.success("Your food is ready! Driver is on the way to pick it up.", { duration: 5000 });
  }, [order?.status]);

  // Restaurant reported a kitchen delay — surface the new ETA to the customer.
  useEffect(() => {
    if (!order) return;
    const total = Number(order.delayed_by_minutes || 0);
    if (total <= 0) { lastDelayRef.current = 0; return; }
    if (lastDelayRef.current == null) { lastDelayRef.current = total; return; }
    if (total > lastDelayRef.current) {
      const added = total - lastDelayRef.current;
      lastDelayRef.current = total;
      toast.warning(
        `Kitchen delay: +${added} min · new ETA ${order.eta_minutes} min`,
        { duration: 6000 },
      );
    }
  }, [order?.delayed_by_minutes, order?.eta_minutes]);

  const driverPosition = useMemo(() => {
    if (!order) return null;
    if (order.driver_lat == null || order.driver_lng == null) return null;
    return { lat: order.driver_lat, lng: order.driver_lng };
  }, [order?.driver_lat, order?.driver_lng]);

  const pickupCoords = order?.pickup_lat != null ? { lat: order.pickup_lat, lng: order.pickup_lng } : null;
  const dropoffCoords = order?.dropoff_lat != null ? { lat: order.dropoff_lat, lng: order.dropoff_lng } : null;

  // Live Google Maps ETA. Uses the DistanceMatrixService while the driver is
  // on the way; falls back to a haversine estimate if the API is unavailable
  // or the driver's GPS is stale (>60s).
  const [liveEta, setLiveEta] = useState(null); // { minutes, distanceKm, source: 'gmaps'|'haversine' }
  useEffect(() => {
    if (!order || !driverPosition || !dropoffCoords) return;
    if (order.status !== "on_the_way") return;

    let cancelled = false;
    const haversineFallback = () => {
      const R = 6371;
      const dLat = (dropoffCoords.lat - driverPosition.lat) * Math.PI / 180;
      const dLng = (dropoffCoords.lng - driverPosition.lng) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 +
                Math.cos(driverPosition.lat * Math.PI / 180) *
                Math.cos(dropoffCoords.lat * Math.PI / 180) *
                Math.sin(dLng / 2) ** 2;
      const km = 2 * R * Math.asin(Math.sqrt(a));
      // Assume urban Grenada avg ~28 km/h
      const minutes = Math.max(1, Math.round((km / 28) * 60));
      return { minutes, distanceKm: km, source: "haversine" };
    };

    const compute = async () => {
      // Prefer Google's live routing when the JS API is loaded on the page.
      if (window.google?.maps?.DistanceMatrixService) {
        try {
          const svc = new window.google.maps.DistanceMatrixService();
          svc.getDistanceMatrix(
            {
              origins: [driverPosition],
              destinations: [dropoffCoords],
              travelMode: window.google.maps.TravelMode.DRIVING,
              drivingOptions: { departureTime: new Date() },
            },
            (res, status) => {
              if (cancelled) return;
              if (status === "OK" && res?.rows?.[0]?.elements?.[0]?.status === "OK") {
                const el = res.rows[0].elements[0];
                const seconds = (el.duration_in_traffic || el.duration).value;
                setLiveEta({
                  minutes: Math.max(1, Math.round(seconds / 60)),
                  distanceKm: el.distance.value / 1000,
                  source: "gmaps",
                });
              } else {
                setLiveEta(haversineFallback());
              }
            },
          );
        } catch {
          setLiveEta(haversineFallback());
        }
      } else {
        setLiveEta(haversineFallback());
      }
    };
    compute();
    const t = setInterval(compute, 30_000); // refresh every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, [order?.status, driverPosition?.lat, driverPosition?.lng, dropoffCoords?.lat, dropoffCoords?.lng]);

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="skeleton h-64 rounded-3xl" />
      </div>
    );
  }

  const currentIdx = STAGES.findIndex((s) => s.key === order.status);
  const currentStage = STAGES[currentIdx] || STAGES[0];
  const chatUnlocked = order.status === "on_the_way";
  const driverAssigned = !!order.driver_id;
  const driver = order.driver || {};
  const driverName = order.driver_name || driver.name || "Kwesi Baptiste";
  const driverPhoto = driver.profile_photo || null;
  const driverVehicle = driver.vehicle_type || "Scooter";
  const driverVehicleDetail = driver.vehicle_details || "Green Honda · GDA-4291";
  const driverRating = driver.rating || 4.9;
  const showLiveMap = ["picked_up", "on_the_way"].includes(order.status) && (pickupCoords || dropoffCoords);
  const driverLocationFresh = order.driver_location_at
    ? (new Date() - new Date(order.driver_location_at)) < 60_000
    : false;
  const effectiveEta = liveEta?.minutes || order.eta_minutes || null;
  // When the driver is en-route, promote the map to true full-screen on
  // desktop too (mobile has always been full-screen). The bottom sheet
  // floats over the map so tracking feels like an app, not a two-column page.
  const immersive = order.status === "on_the_way";

  return (
    <div className={immersive ? "fixed inset-0 z-30" : "fixed inset-0 z-30 md:relative md:z-0"}>
      {/* Full-screen map */}
      <div
        className={immersive ? "absolute inset-0" : "absolute inset-0 md:relative md:h-[60vh]"}
        data-testid="customer-tracking-map"
      >
        {showLiveMap ? (
          <DriverDeliveryMap
            pickupAddress={order.restaurant_name || order.restaurant_address}
            dropoffAddress={order.address}
            pickupCoords={pickupCoords}
            dropoffCoords={dropoffCoords}
            driverPosition={driverPosition}
            useDriverGps={false}
            driverName={driverName.split(" ")[0]}
            dark
            height="100%"
          />
        ) : (
          <MapMock height="100%" driverName={driverName.split(" ")[0]} />
        )}
        <button
          data-testid="tracking-back-btn"
          onClick={() => navigate("/orders")}
          className="absolute top-4 left-4 z-10 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="absolute top-4 right-4 z-10 bg-white shadow-lg rounded-full px-4 py-2 text-xs">
          <span className="text-neutral-500">Order </span>
          <span className="font-mono font-bold">#{order.order_id.slice(-6).toUpperCase()}</span>
        </div>
        {order.status === "on_the_way" && driverLocationFresh && (
          <div
            className="absolute top-20 left-1/2 -translate-x-1/2 z-10 bg-eatup-green text-white text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
            data-testid="live-driver-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            Driver is moving — live GPS
          </div>
        )}

        {/* Live Status Bar — sits at the bottom of the map, above the sheet, and
            shows the real minutes-away ETA from Google Maps Distance Matrix. */}
        {order.status === "on_the_way" && (
          <LiveStatusBar
            eta={effectiveEta}
            source={liveEta?.source}
            distanceKm={liveEta?.distanceKm}
            fresh={driverLocationFresh}
            immersive={immersive}
          />
        )}
      </div>

      {/* Bottom sheet */}
      <div className={
        immersive
          ? `absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl transition-[max-height] duration-500 overflow-hidden ${expanded ? "max-h-[85vh]" : "max-h-[60vh]"} md:max-w-md md:right-4 md:left-auto md:bottom-4 md:rounded-3xl md:max-h-[calc(100vh-2rem)] flex flex-col`
          : `absolute md:relative bottom-0 left-0 right-0 md:mt-6 bg-white rounded-t-3xl md:rounded-3xl shadow-2xl md:shadow-none transition-[max-height] duration-500 overflow-hidden ${expanded ? "max-h-[85vh]" : "max-h-[60vh]"} md:max-h-none flex flex-col`
      }>
        <button
          onClick={() => setExpanded(!expanded)}
          className={immersive ? "w-full flex justify-center pt-2 pb-1 shrink-0" : "md:hidden w-full flex justify-center pt-2 pb-1 shrink-0"}
          data-testid="sheet-toggle"
        >
          <div className="w-12 h-1.5 rounded-full bg-neutral-200" />
        </button>

        <div className="px-6 pt-2 pb-4 shrink-0">
          <div className="text-xs uppercase tracking-[0.3em] text-eatup-green font-bold">Arriving in</div>
          <div className="flex items-baseline justify-between mt-1">
            <h1 className="font-display text-4xl font-extrabold tabular">
              {order.status === "delivered" ? "Delivered" : `${order.eta_minutes} min`}
            </h1>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-neutral-500 font-semibold flex items-center gap-1"
              data-testid="details-toggle"
            >
              {expanded ? "Hide" : "Details"}
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
          <div className="mt-1 text-sm text-neutral-600 font-medium">
            <span className="text-eatup-charcoal font-bold">{currentStage.label}.</span> {currentStage.sub}.
          </div>

          {/* Ready banner — pops the moment the kitchen flips the order to `ready` */}
          {order.status === "ready" && (
            <div
              data-testid="customer-ready-banner"
              className="mt-3 rounded-2xl bg-eatup-yellow/20 border border-eatup-yellow/60 p-3 flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-xl bg-eatup-yellow text-eatup-charcoal flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-eatup-charcoal">
                  Your food is ready!
                </div>
                <div className="text-xs text-neutral-600 truncate">
                  The driver is heading to the counter — pickup imminent.
                </div>
              </div>
            </div>
          )}

          {/* Kitchen delay banner — surfaces the accumulated delay minutes */}
          {Number(order.delayed_by_minutes || 0) > 0 && !["picked_up", "on_the_way", "delivered"].includes(order.status) && (
            <div
              data-testid="customer-delay-banner"
              className="mt-3 rounded-2xl bg-red-50 border border-red-200 p-3 flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-xl bg-red-500 text-white flex items-center justify-center shrink-0">
                <Loader2 className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-red-700">
                  Kitchen running a bit late — sorry!
                </div>
                <div className="text-xs text-red-600 truncate">
                  New ETA {order.eta_minutes} min · added +{order.delayed_by_minutes} min from the kitchen.
                </div>
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div className="mt-4 flex items-center gap-1.5">
            {STAGES.map((s, i) => (
              <div
                key={s.key}
                className={`h-1.5 flex-1 rounded-full transition ${i <= currentIdx ? "bg-eatup-green" : "bg-neutral-200"}`}
              />
            ))}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 pb-8 space-y-4">
          {/* Delivery proof photo (customer view) */}
          {order.proof_of_delivery_image && (
            <div className="rounded-3xl overflow-hidden border-2 border-eatup-green/40 bg-eatup-green/5" data-testid="customer-proof-card">
              <img src={order.proof_of_delivery_image} alt="Your delivered food" className="w-full aspect-video object-cover" />
              <div className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-eatup-green text-white flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold">Delivered — enjoy!</div>
                  <div className="text-xs text-neutral-500">Photo from your driver at the drop-off</div>
                </div>
                {order.status === "delivered" && order.customer_rating == null && (
                  <button
                    onClick={() => setShowRating(true)}
                    data-testid="rate-driver-btn"
                    className="text-xs font-bold bg-eatup-yellow text-eatup-charcoal rounded-full px-3 py-1.5 hover:bg-eatup-yellow/80 transition"
                  >
                    Rate driver
                  </button>
                )}
                {order.customer_rating != null && (
                  <div className="text-xs font-bold text-eatup-green flex items-center gap-1" data-testid="rating-given-badge">
                    <Star className="w-3.5 h-3.5 fill-eatup-yellow text-eatup-yellow" />
                    {order.customer_rating}/5
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pickup photo (customer view) */}
          {order.pickup_photo_image && (
            <div className="rounded-3xl overflow-hidden border-2 border-eatup-yellow/50 bg-eatup-yellow/10" data-testid="customer-pickup-photo-card">
              <div className="px-4 pt-4 pb-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-eatup-yellow text-eatup-charcoal flex items-center justify-center">
                  <Loader2 className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold">Your order has been picked up!</div>
                  <div className="text-xs text-neutral-500">See the photo below.</div>
                </div>
              </div>
              <img src={order.pickup_photo_image} alt="Your order leaving the kitchen" className="w-full aspect-video object-cover" />
            </div>
          )}

          {/* Driver card — the whole card is now a button that opens a detailed
              driver profile drawer with large Call + Message CTAs. */}
          <button
            onClick={() => driverAssigned && setDriverProfileOpen(true)}
            data-testid="driver-info-card"
            disabled={!driverAssigned}
            className="w-full bg-eatup-charcoal text-white rounded-2xl p-4 flex items-center gap-3 hover:bg-black transition text-left disabled:opacity-70 disabled:cursor-not-allowed active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-eatup-green"
          >
            {driverPhoto ? (
              <img
                src={driverPhoto}
                alt={driverName}
                className="w-14 h-14 rounded-full object-cover ring-2 ring-eatup-yellow/60 shadow-md shrink-0"
                data-testid="driver-avatar-photo"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-eatup-green text-white flex items-center justify-center font-bold text-lg shrink-0 ring-2 ring-white/20">
                {driverName.slice(0, 1)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold truncate" data-testid="driver-name">{driverName}</div>
              <div className="text-[11px] text-neutral-400 flex items-center gap-1.5 flex-wrap mt-0.5">
                <span className="flex items-center gap-1 text-eatup-yellow">
                  <Star className="w-3 h-3 fill-eatup-yellow" /> {driverRating}
                </span>
                <span className="text-neutral-500">·</span>
                <span
                  data-testid="driver-vehicle-type"
                  className="inline-flex items-center gap-1 rounded-full bg-eatup-green/15 text-eatup-green border border-eatup-green/30 px-2 py-0.5 font-bold uppercase tracking-widest"
                >
                  {driverVehicle === "Car" ? <Car className="w-3 h-3" /> : <Bike className="w-3 h-3" />}
                  {driverVehicle}
                </span>
                <span className="text-neutral-500 truncate">{driverVehicleDetail}</span>
              </div>
            </div>
            <div
              data-testid="call-driver-btn"
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0"
              aria-hidden
            >
              <Phone className="w-4 h-4" />
            </div>
            <div
              data-testid="chat-driver-btn"
              className={`relative inline-flex items-center gap-2 rounded-full px-4 h-10 text-xs font-black uppercase tracking-widest ${
                chatUnlocked && driverAssigned
                  ? "bg-eatup-green text-white shadow-md"
                  : "bg-white/10 text-neutral-400"
              }`}
              aria-hidden
            >
              {chatUnlocked && driverAssigned ? <MessageCircle className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />}
              Message
              {chatUnlocked && driverAssigned && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-eatup-yellow border-2 border-eatup-charcoal" />
              )}
            </div>
          </button>

          {/* Restaurant summary */}
          <div className="flex items-center gap-3">
            <img src={order.restaurant_image} alt={order.restaurant_name} className="w-14 h-14 rounded-xl object-cover" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">From</div>
              <div className="font-semibold">{order.restaurant_name}</div>
            </div>
          </div>

          {/* Timeline (compact) */}
          <div className="border border-neutral-100 rounded-2xl p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold mb-3">Progress</div>
            <div className="space-y-3">
              {STAGES.map((s, i) => {
                const done = i < currentIdx;
                const current = i === currentIdx;
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    {done ? (
                      <CheckCircle2 className="w-5 h-5 text-eatup-green shrink-0" />
                    ) : current ? (
                      <Loader2 className="w-5 h-5 text-eatup-green animate-spin shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-neutral-300 shrink-0" />
                    )}
                    <div className={`text-sm ${current ? "font-bold text-eatup-charcoal" : done ? "text-neutral-500" : "text-neutral-400"}`}>{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Order items */}
          <div className="border border-neutral-100 rounded-2xl p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold mb-3">Your order</div>
            <div className="space-y-2 text-sm">
              {order.items.map((it) => (
                <div key={it.item_id} className="flex items-center justify-between">
                  <span>{it.quantity}× {it.name}</span>
                  <span className="text-neutral-500 tabular">${(it.price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="h-px bg-neutral-100 my-2" />
              <div className="flex items-center justify-between font-bold">
                <span>Total</span>
                <span className="tabular" data-testid="tracking-total">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {chatOpen && (
        <OrderChatDrawer
          orderId={order.order_id}
          meRole="customer"
          counterpartName={driverName}
          onClose={() => setChatOpen(false)}
          disabled={!chatUnlocked}
          disabledReason="Chat opens once the driver is on the way."
        />
      )}

      {showRating && (
        <RatingModal
          orderId={order.order_id}
          driverName={driverName.split(" ")[0]}
          onClose={() => setShowRating(false)}
          onSubmitted={() => { setShowRating(false); load(); }}
        />
      )}

      {driverProfileOpen && (
        <DriverProfileDrawer
          driver={{
            name: driverName,
            photo: driverPhoto,
            vehicle: driverVehicle,
            vehicleDetail: driverVehicleDetail,
            rating: driverRating,
            phone: driver.phone,
          }}
          order={order}
          canChat={chatUnlocked && driverAssigned}
          onClose={() => setDriverProfileOpen(false)}
          onMessage={() => { setDriverProfileOpen(false); setChatOpen(true); }}
        />
      )}
    </div>
  );
}


function LiveStatusBar({ eta, source, distanceKm, fresh, immersive }) {
  // Bottom-of-map live ETA bar. Uses Google Distance Matrix when available
  // (source==='gmaps') and falls back to a straight-line haversine estimate.
  if (!eta) return null;
  const label = eta <= 1 ? "less than a minute" : `${eta} minute${eta !== 1 ? "s" : ""}`;
  // In immersive (full-screen) mode the bar floats just above the collapsed
  // bottom sheet (~60vh). In legacy split mode it hugs the map/sheet seam.
  return (
    <div
      data-testid="live-status-bar"
      className="absolute left-0 right-0 z-20 pointer-events-none"
      style={{ bottom: immersive ? "calc(60vh + 0.75rem)" : "calc(60vh - 3.75rem)" }}
    >
      <div className="max-w-md mx-auto px-4">
        <div className="pointer-events-auto rounded-full bg-eatup-charcoal/95 backdrop-blur border border-white/10 shadow-2xl px-4 py-2.5 flex items-center gap-3">
          <div className="relative w-9 h-9 rounded-full bg-eatup-green/20 flex items-center justify-center shrink-0">
            <span className="absolute inset-0 rounded-full bg-eatup-green/30 animate-ping" />
            <Navigation className="relative w-4 h-4 text-eatup-green" />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[10px] uppercase tracking-widest font-black text-eatup-yellow leading-none">
              {fresh ? "Live · updated just now" : "Live · reconnecting…"}
            </div>
            <div className="mt-0.5 font-display font-bold text-sm truncate" data-testid="live-status-text">
              Driver is <span className="text-eatup-green tabular font-black">{label}</span> away
              {distanceKm != null && (
                <span className="text-neutral-400 font-medium"> · {distanceKm.toFixed(1)} km</span>
              )}
            </div>
          </div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold shrink-0" data-testid="live-status-source">
            {source === "gmaps" ? "Google ETA" : "Estimate"}
          </div>
        </div>
      </div>
    </div>
  );
}
